// src/pages/WeeklyPolicyTrends.jsx
import React, { useEffect, useState, useContext, useCallback } from "react";
import { Card, CardBody, Spinner } from "reactstrap";
import ReactApexChart from "react-apexcharts";
import { request } from "../../utils/request";
import { ColorModeContext } from "../../index";
import "./dashboard.scss";

const WeeksToShow = 4;

// Utility: get ISO week + year for a Date
function getISOWeekAndYear(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return { week: weekNo, year: d.getUTCFullYear() };
}

// Build last N iso week/year pairs
function buildLastNWeeks(n) {
  const out = [];
  const now = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(now.getDate() - i * 7);
    out.push(getISOWeekAndYear(d));
  }
  return out;
}

/* ------------------------------
   Inject the exact same lightweight
   polish CSS used by SalesAnalytics/MiniWidgets
   so this component's card looks identical.
   ------------------------------ */
const injectMiniWidgetStyles = () => {
  if (document.getElementById("mini-widget-polish")) return;
  const css = `
    .mw-light-root { --bg: #fff; --tile-bg: #f9fafc; --muted: #666; --text: #0b1220; --tile-border: rgba(6,15,34,0.04); --icon-ghost: rgba(3,7,18,0.03); --shadow: 0 1px 2px rgba(0,0,0,0.06); }
    .mw-dark-root { --bg: #252b3b; --tile-bg: #2f3649; --muted: #c9c9c9; --text: #f5f5f5; --tile-border: rgba(255,255,255,0.05); --icon-ghost: rgba(255,255,255,0.03); --shadow: 0 1px 3px rgba(0,0,0,0.35); }

    .mini-tile {
      position: relative;
      overflow: hidden;
      border-radius: 10px;
      display: flex;
      align-items: center;
      justify-content: space-between;
      transition: transform 160ms ease, box-shadow 160ms ease;
    }
    .mini-tile::after {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02));
      mix-blend-mode: overlay;
    }

    .mini-left { z-index: 1; flex: 1; overflow: hidden; padding-right: 8px; min-width: 0; }
    .mini-title { margin: 0; font-size: 0.88rem; color: var(--text); font-weight: 600; white-space: nowrap; text-overflow: ellipsis; overflow: hidden; }
    .mini-value { margin: 4px 0 0; font-size: 1rem; color: var(--text); font-weight: 600; }
    .mini-desc { display: block; margin-top: 6px; color: var(--muted); font-size: 0.72rem; }

    .mini-right { z-index: 1; display:flex; flex-direction: column; align-items: flex-end; gap: 6px; min-width: 44px; margin-left: 0.4rem; }
    .mini-icon-circle { width: 36px; height: 36px; border-radius: 8px; display:flex; align-items:center; justify-content:center; box-shadow: inset 0 -6px 14px rgba(0,0,0,0.04); background: var(--icon-ghost); color: var(--text); flex-shrink: 0; }

    .mini-live-badge {
      position: absolute;
      right: 8px;
      top: 8px;
      z-index: 2;
      font-size: 10px;
      padding: 4px 8px;
      border-radius: 999px;
      font-weight: 700;
      background: linear-gradient(90deg, rgba(0,191,99,0.12), rgba(0,191,99,0.04));
      color: #036b27;
      box-shadow: 0 2px 6px rgba(3, 107, 39, 0.06);
      pointer-events: none;
    }

    .polish-card {
      border-radius: 12px;
      overflow: visible;
      position: relative;
    }
    .polish-card .polish-body {
      padding: 0.9rem;
      box-sizing: border-box;
    }
    .polish-card .card-sheen::after {
      content: "";
      position: absolute;
      inset: 0;
      pointer-events: none;
      background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02));
      mix-blend-mode: overlay;
      border-radius: inherit;
    }

    .polish-header { display:flex; align-items:center; justify-content:space-between; gap:12px; margin-bottom:6px; }
    .polish-title { margin:0; font-size:1rem; font-weight:600; color:var(--text); }

    .polish-chart-wrap { min-height:200px; display:flex; align-items:center; justify-content:center; }

    .polish-legend p { margin:0; font-size:0.85rem; color:var(--text); }
    .polish-legend .value { font-weight:600; font-size:1rem; color:var(--text); }

    .mw-light-root .polish-card { background: linear-gradient(180deg, rgba(255,255,255,0.6), rgba(243,247,255,0.7)); border: 1px solid var(--tile-border); box-shadow: var(--shadow); }
    .mw-dark-root .polish-card { background: linear-gradient(180deg, rgba(255,255,255,0.02), rgba(0,0,0,0.02)); border: 1px solid var(--tile-border); box-shadow: var(--shadow); color: var(--text); }

    @media (max-width:720px) {
      .mini-title { white-space: normal; }
      .polish-header { flex-direction: column; align-items: flex-start; gap:6px; }
    }
  `;
  const s = document.createElement("style");
  s.id = "mini-widget-polish";
  s.appendChild(document.createTextNode(css));
  document.head.appendChild(s);
};

const WeeklyPolicyTrends = () => {
  const { mode } = useContext(ColorModeContext);
  const isDark = mode === "dark";

  // inject shared polish styles so card matches SalesAnalytics/MiniWidgets
  useEffect(() => {
    injectMiniWidgetStyles();
  }, []);

  const [loading, setLoading] = useState(false);
  const [series, setSeries] = useState([]);
  const [categories, setCategories] = useState([]);
  const [fetchedAt, setFetchedAt] = useState(null);

  // Color palettes
  const paletteLight = ["#3F51B5","#00BCD4","#E91E63","#FFC107","#FF5722","#4CAF50","#9C27B0","#03A9F4","#795548","#9E9E9E"];
  const paletteDark = ["#6DD3C2","#86A8E7","#D16BA5","#F9D56E","#F08A5D","#B8F2E6","#C9BBCF","#A3DE83","#FBC78D","#8B9CB2"];
  const colors = isDark ? paletteDark : paletteLight;

  const transformToSeries = (rows, weeksList) => {
    const weekKeys = weeksList.map(w => `${w.year}-${w.week}`);
    const deptSet = new Set();
    const map = new Map();

    for (const r of rows) {
      const dept = (r.department || "Unassigned").toString();
      deptSet.add(dept);
      const key = `${r.year}-${r.week}-${dept}`;
      map.set(key, Number(r.violationCount || 0));
    }

    const departments = Array.from(deptSet).sort();
    const seriesOut = departments.map((dept, idx) => {
      const data = weeksList.map(w => map.get(`${w.year}-${w.week}-${dept}`) || 0);
      return { name: dept, data, color: colors[idx % colors.length] };
    });

    const categories = weeksList.map(w => `W${w.week} ${w.year}`);
    return { seriesOut, categories };
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const weeksList = buildLastNWeeks(WeeksToShow);
      const path = `analytics/policy-violations-weekly?weeks=${WeeksToShow}`;
      const res = await request(path, { method: "GET" });
      const rows = Array.isArray(res) ? res : [];

      const normalized = rows.map(r => ({
        department: (r.department || "Unassigned").toString(),
        week: Number(r.week),
        year: Number(r.year),
        violationCount: Number(r.violationCount || 0)
      }));

      const { seriesOut, categories } = transformToSeries(normalized, weeksList);
      setSeries(seriesOut);
      setCategories(categories);
      setFetchedAt(new Date());
    } catch (err) {
      console.error("WeeklyPolicyTrends fetch error:", err);
      setSeries([]);
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, [mode]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const options = {
    chart: {
      type: "bar",
      stacked: true,
      toolbar: { show: false },
      zoom: { enabled: false },
      foreColor: isDark ? "#E0E6ED" : "#475569",
      animations: { enabled: true, easing: "easeinout", speed: 700 },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "45%",
        borderRadius: 6,
      }
    },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 1, colors: ["transparent"] },
    xaxis: { categories, axisBorder: { show: false }, axisTicks: { show: false } },
    yaxis: { title: { text: "Policy Violations" } },
    legend: { position: "top", horizontalAlign: "center" },
    fill: { opacity: 0.9 },
    tooltip: { shared: true, intersect: false, y: { formatter: (val) => `${val} violations` } },
    colors: series.map(s => s.color).length ? series.map(s => s.color) : colors,
    grid: {
      borderColor: isDark ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)",
      strokeDashArray: 4,
    },
  };

  // root class to pick theme vars from injected CSS
  const rootClass = mode === "dark" ? "mw-dark-root" : "mw-light-root";

  return (
    <div className={`widget-card ${rootClass}`} style={{ width: "100%" }}>
      <Card
        className="polish-card"
        style={{
          height: "100%",
          backgroundColor: isDark ? "#252b3b" : "#fff",
          border: isDark ? "none" : "1px solid #e0e0e0",
          borderRadius: "12px",
        }}
      >
        <div className="card-sheen" style={{ position: "absolute", inset: 0, borderRadius: 12, pointerEvents: "none" }} />

        <CardBody
          className={`polish-body ${isDark ? "text-white" : ""}`}
          style={{ display: "flex", flexDirection: "column", minHeight: 0, padding: 0, height: "100%", boxSizing: "border-box" }}
        >
          <div
            className="polish-header"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0.9rem 0.9rem 0 0.9rem",
            }}
          >
            <h5
              className="polish-title"
              style={{
                margin: 0,
                textAlign: "left",
                flexGrow: 1,
              }}
            >
              Policy Violation Trends (Last {WeeksToShow} Weeks)
            </h5>
          </div>

          <div className="polish-chart-wrap" style={{ padding: "0 0.9rem 0.4rem 0.9rem", minHeight: 200 }}>
            {loading ? (
              <div className="d-flex align-items-center justify-content-center" style={{ height: 280 }}>
                <Spinner color={isDark ? "light" : "primary"} />
              </div>
            ) : series.length === 0 || categories.length === 0 ? (
              <div className="d-flex align-items-center justify-content-center" style={{ height: 280 }}>
                <div className={isDark ? "text-muted" : "text-secondary"}>No data for the selected weeks</div>
              </div>
            ) : (
              <ReactApexChart options={options} series={series} type="bar" height={320} />
            )}
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default WeeklyPolicyTrends;
